/**
 * A decisão humana: participar ou passar.
 *
 * A rota existe e está testada desde sempre — `POST /api/licitacoes/{id}/decisao`
 * — e nenhuma tela a chamava. Por isso o banco tinha 0 decisões: não é que
 * ninguém decidisse, é que não havia por onde.
 *
 * ── Duas coisas que o desenho precisa respeitar ──────────────────────────
 *
 * **A decisão não é a recomendação.** As abas de Oportunidades já se chamam
 * "Aprovados" e "Passar", mas aquilo é o que a IA achou. Aqui é o que a pessoa
 * decidiu, e as duas convivem na mesma tela — por isso o vocabulário é outro
 * ("Vou participar") e o selo é visualmente distinto do `SeloRecomendacao`.
 *
 * **É append-only.** Mudar de ideia grava uma segunda linha e as duas ficam,
 * porque sobrescrever apagaria o caso mais informativo: aquele em que alguém
 * reviu o próprio veredito. Então isto NÃO é um alternador — cada clique é um
 * registro novo, e o que se vê é o mais recente.
 *
 * O QUE VEM DEPOIS DE APROVAR não mora mais aqui (D13, 2026-10-05): a
 * preparação da documentação, a entrada na disputa e o resultado são o
 * componente `Disputa`, logo abaixo no painel.
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type LicitacaoDetalhe, type NovaDecisao } from "../api/cliente";
import { useSessao } from "../sessao";
import css from "./Decisao.module.css";

/** O mesmo mínimo da API (`modelos.MOTIVO_MINIMO`) e do banco (0099). Aqui
 * só serve para o botão não se acender antes de o servidor aceitar. */
const MOTIVO_MINIMO = 10;

/** `2026-09-05T03:00:00Z` -> `05/09/2026`. */
function dia(iso: string | null | undefined) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("pt-BR");
}

export function Decisao({ d }: { d: LicitacaoDetalhe }) {
  const cliente = useQueryClient();
  const { podeOperar } = useSessao();
  // O MOTIVO ESCRITO SOBREVIVE (19/10): à troca de aba, e à abertura da página
  // dedicada — o painel e a página são dois componentes, e o que se digitou num
  // não pode se perder ao passar para o outro. Mora no `sessionStorage`, POR
  // OPORTUNIDADE: a chave leva o id, e o rascunho de um cartão nunca aparece
  // em outro. Some quando a decisão é gravada, ou quando o campo fica vazio.
  const chave = `rascunho-decisao:${d.id}`;
  const [motivo, setMotivo] = useState(() => {
    try {
      return sessionStorage.getItem(chave) ?? "";
    } catch {
      return "";
    }
  });
  useEffect(() => {
    try {
      if (motivo) sessionStorage.setItem(chave, motivo);
      else sessionStorage.removeItem(chave);
    } catch {
      /* navegação privada ou cota cheia: o campo continua valendo, só não sobrevive */
    }
  }, [chave, motivo]);
  // Na já decidida, o campo do motivo só abre quando alguém pede para trocar
  // a decisão: reprovar a aprovada, ou aprovar a reprovada.
  const [trocando, setTrocando] = useState(false);

  const decidir = useMutation({
    mutationFn: (veredito: NovaDecisao["veredito"]) =>
      // `pre_aprovacao`, não `final`: esta é a triagem rápida sobre o objeto e
      // a ficha, que é a etapa 4 do funil. A `final` é a decisão com o dossiê
      // do edital na mão — etapa 8, que ainda não existe. Gravar `final` aqui
      // faria o histórico afirmar que alguém leu o edital antes de decidir.
      api.decidir(d.id, {
        veredito,
        etapa: "pre_aprovacao",
        motivo: motivo.trim(),
      }),
    onSuccess: () => {
      setMotivo("");
      setTrocando(false);
      // A licitação aberta, a lista e as facetas mostram a decisão — sem
      // invalidar as três, o painel diz "decidida" enquanto a lista ao lado
      // continua com a contagem antiga de pendentes.
      void cliente.invalidateQueries({ queryKey: ["licitacao", d.id] });
      // O histórico da MESMA oportunidade, nos dois modos, mostra a decisão nova.
      void cliente.invalidateQueries({ queryKey: ["decisoes", d.id] });
      void cliente.invalidateQueries({ queryKey: ["licitacoes"] });
      void cliente.invalidateQueries({ queryKey: ["facetas"] });
    },
  });

  const decidida = d.decisao !== "pendente";
  const ocupado = decidir.isPending;
  // O MOTIVO É OBRIGATÓRIO (pedido do Pedro, 2026-09-28): "isso vai fazer com
  // que o sistema entenda melhor o porquê de tais oportunidades serem boas ou
  // ruins para a empresa". Sem ele os dois botões ficam apagados.
  const faltam = Math.max(0, MOTIVO_MINIMO - motivo.trim().length);
  const semMotivo = faltam > 0;

  if (!podeOperar) {
    return (
      <p className={css.aviso}>
        {decidida
          ? `${d.decisao === "aprovada" ? "Aprovada" : "Passada"} por ${d.decidido_por}.`
          : "Ainda sem decisão."}{" "}
        Seu acesso é de leitura — decidir é restrito a operadores.
      </p>
    );
  }

  // A JÁ DECIDIDA NÃO PEDE A DECISÃO DE NOVO (pedidos do Pedro, 2026-10-05):
  // "em uma já aprovada, isso aqui não é necessário. Só um botão para
  // reprovar, caso seja necessário" — e depois: "faça o mesmo na reprovada,
  // só um botão para aprovar". O formulário inteiro — o campo, a ajuda, o
  // botão apagado do que ela já é — ocupava o lugar do que vem depois. Fica a
  // faixa com quem decidiu e o motivo, e UM botão, o do sentido contrário.
  //
  // O motivo continua obrigatório (a API e o banco recusam sem ele): o campo
  // abre só depois do clique, e a troca é confirmada ali.
  if (decidida) {
    const aprovada = d.decisao === "aprovada";
    const troca: NovaDecisao["veredito"] = aprovada ? "descartada" : "aprovada";
    return (
      <div className={css.tudo}>
        <p className={`${css.atual} ${aprovada ? css.sim : css.nao}`}>
          <b>{aprovada ? "Vamos participar" : "Descartamos"}</b>
          <span>
            {d.decidido_por}
            {dia(d.decidido_em) ? ` · ${dia(d.decidido_em)}` : ""}
          </span>
        </p>
        {d.decisao_motivo && <p className={css.motivoDado}>{d.decisao_motivo}</p>}

        {trocando ? (
          <>
            <label className={css.rotuloMotivo} htmlFor={`motivo-${d.id}`}>
              {aprovada ? "Por que reprovar agora?" : "Por que aprovar agora?"}
            </label>
            <textarea
              id={`motivo-${d.id}`}
              className={css.motivo}
              value={motivo}
              onChange={(e) => setMotivo(e.currentTarget.value)}
              placeholder={
                aprovada
                  ? "Ex.: o edital exige equipe local; ou: o prazo ficou curto para montar a proposta."
                  : "Ex.: a gestão decidiu entrar; ou: achamos o atestado que comprova o objeto."
              }
              rows={3}
              required
              autoFocus
              aria-describedby={`motivo-ajuda-${d.id}`}
              disabled={ocupado}
            />
            <p id={`motivo-ajuda-${d.id}`} className={css.ajudaMotivo} aria-live="polite">
              {semMotivo
                ? `Obrigatório — faltam ${faltam} caractere${faltam === 1 ? "" : "s"}.`
                : `O motivo fica gravado junto com a ${aprovada ? "reprovação" : "aprovação"}.`}
            </p>
            <div className={css.botoes}>
              <button
                type="button"
                // A cor segue o sentido: aprovar é o verde de sempre, reprovar
                // é o contorno apagado.
                className={aprovada ? css.passar : css.participar}
                onClick={() => decidir.mutate(troca)}
                disabled={ocupado || semMotivo}
              >
                {ocupado ? "Gravando…" : aprovada ? "Confirmar reprovação" : "Confirmar aprovação"}
              </button>
              <button
                type="button"
                className={css.passar}
                onClick={() => {
                  setTrocando(false);
                  setMotivo("");
                }}
                disabled={ocupado}
              >
                Cancelar
              </button>
            </div>
          </>
        ) : (
          <button type="button" className={css.trocar} onClick={() => setTrocando(true)}>
            {aprovada ? "Reprovar" : "Aprovar"}
          </button>
        )}

        {decidir.isError && (
          <p className={css.erro}>
            {decidir.error instanceof Error
              ? decidir.error.message
              : "não deu para gravar a decisão"}
          </p>
        )}
      </div>
    );
  }

  // A PENDENTE: o formulário inteiro, com os dois botões.
  return (
    <div className={css.tudo}>
      {/* O motivo fica ANTES dos botões: escrito depois de clicar, ele não
          teria como entrar no registro que acabou de ser gravado — e a decisão
          é append-only, não editável. */}
      <label className={css.rotuloMotivo} htmlFor={`motivo-${d.id}`}>
        Por que participar ou reprovar?
      </label>
      <textarea
        id={`motivo-${d.id}`}
        className={css.motivo}
        value={motivo}
        onChange={(e) => setMotivo(e.currentTarget.value)}
        placeholder="Ex.: temos atestado de capacitação para professores nesta escala; ou: o objeto é obra, fora do que a Nectar faz."
        rows={3}
        required
        aria-describedby={`motivo-ajuda-${d.id}`}
        disabled={ocupado}
      />
      <p id={`motivo-ajuda-${d.id}`} className={css.ajudaMotivo} aria-live="polite">
        {semMotivo
          ? `Obrigatório — faltam ${faltam} caractere${faltam === 1 ? "" : "s"}. É o que ensina ao sistema o que serve e o que não serve para a Nectar.`
          : "Obrigado — o motivo fica gravado junto com a decisão."}
      </p>

      <div className={css.botoes}>
        <button
          type="button"
          className={css.participar}
          onClick={() => decidir.mutate("aprovada")}
          disabled={ocupado || semMotivo}
        >
          {ocupado ? "Gravando…" : "Vou participar"}
        </button>
        <button
          type="button"
          className={css.passar}
          // "REPROVAR" (2026-09-16), porque a aba para onde a decisao manda
          // passou a se chamar "Reprovadas" — e "Descartadas" virou o nome da
          // faixa de score 50-69, calculada pela maquina. Com "Descartar" aqui,
          // clicar pareceria mandar o card para a aba errada. O banco continua
          // gravando `descartada`; so a palavra da tela mudou.
          onClick={() => decidir.mutate("descartada")}
          disabled={ocupado || semMotivo}
        >
          {ocupado ? "Gravando…" : "Reprovar"}
        </button>
      </div>

      {decidir.isError && (
        <p className={css.erro}>
          {decidir.error instanceof Error
            ? decidir.error.message
            : "não deu para gravar a decisão"}
        </p>
      )}
    </div>
  );
}
